import React, { FC, useEffect, useState } from "react";
import { Container, Alert, ModalDialog } from "../../../components";
import _find from "lodash/find";
import _size from "lodash/size";
import { connect, useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { GTM_ECOMMERCE_STEP, MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS, NEW_ACCOUNT_VALUE } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { ICartItem, RootState, StepIndicatorType } from "../../../../../models";
import { AccountIdWithType } from "../../../../../models/state";
import { getErrorMessage, isMinSideSimdealOrder as isMinSideSimdealOrderSelector } from "../../../app/state/selectors";
import { bankIdHandleDsfMismatch } from "../../../authentication/state/actions";
import {
    isBankIdVerified as isBankIdVerifiedSelector,
    isBankIdMandatory as isBankIdMandatorySelector,
    isBankIdAvailable as isBankIdAvailableSelector,
} from "../../../authentication/state/selectors";
import { trackCheckout } from "../../../google-analytics";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import { navigate } from "../../../navigation/state/actions";
import BankIdSection from "../../components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import ChooseBanModal from "../../components/molecules/ChooseBanModal/ChooseBanModal";
import ContactCard from "../../components/molecules/ContactCard/ContactCard";
import CreditCheckModal from "../../components/molecules/CreditCheckModal/CreditCheckModal";
import CheckoutPage from "../../components/organisms/CheckoutPage/CheckoutPage";
import ContactInfoForm from "../../components/organisms/ContactInfoForm/ContactInfoForm";
import DsfMismatchForm from "../../components/organisms/DsfMismatchForm/DsfMismatchForm";
import PersonalDetailsForm from "../../components/organisms/PersonalDetailsForm/PersonalDetailsForm";
import MobilPantNotificationWarning from "../../components/organisms/MobilPantNotificationWarning/MobilPantNotificationWarning";
import { emptyOrder, setActiveSubscription } from "../../state/actions/cartActions";
import { setPersonalDetails, checkBan } from "../../state/actions/personalDetailsActions";
import { resetOrder } from "../../state/actions/phoneNumberActions";

import {
    getAccountsWithSubscriptions,
    hasExternalLegalOwner,
    getInitiatedBy,
    getExternalShowB2BFamilyOffer,
    getAccountId,
} from "../../state/selectors/accountSelectors";
import {
    lastAddedCartItem as lastAddedCartItemSelector,
    hasOnlyServiceSubscriptions as hasOnlyServiceSubscriptionsSelector,
} from "../../state/selectors/cartSelectors";
import * as COMBINED_SELECTORS from "../../state/selectors/combinedSelectors";
import * as CUSTOMER_INFO_SELECTORS from "../../state/selectors/customerInfoSelectors";
import { canContinuePersonalDetails as canContinuePersonalDetailsSelector } from "../../state/selectors/formSelectors";
import getStepIndicatorData from "../../state/selectors/getStepIndicatorData";
import {
    getNumberSelectedAtPDP,
    getSubscriptions,
    hasSubscriptions,
    hasOnlyPrepaidSubscriptions as hasOnlyPrepaidSubscriptionsSelector,
} from "../../state/selectors/subscriptionsSelectors";
import * as VERIFICATION_SELECTORS from "../../state/selectors/verificationSelectors";
import TEXT from "../../text-consts";
import "./PersonalDetails.less";

interface StateProps {
    accountId?: string;
    accountIdsWithTypes: AccountIdWithType[];
    canContinuePersonalDetails: boolean;
    creditCheckMessage: string;
    hasBroadbandSubscriptions: boolean;
    hasExternalLegal: boolean;
    hasOnlyPrepaidSubscriptions: boolean;
    hasOnlyServiceSubscriptions: boolean;
    hasSubscription: boolean;
    hideExistingPhoneNumberArea: boolean;
    initiatedBy: string;
    isMinSideSimdealOrder: boolean;
    isAllowedToProceed: boolean;
    isBankIdAvailable: boolean;
    isBankIdMandatory: boolean;
    isBankIdVerified: boolean;
    isCreditCheckPerformed: boolean;
    isCreditCheckRequired: boolean;
    isDsfMismatch: boolean;
    isFetchingData: boolean;
    isHardwareOnly: boolean;
    isHwOnlyBankid: boolean;
    lastAddedCartItem: ICartItem;
    legalOwnerAddress: string;
    legalOwnerCity: string;
    legalOwnerFullName: string;
    logicalErrorMessage?: string;
    numberSelectedAtPDP: string | undefined;
    shouldOnlyShowSlideoutCart: boolean;
    showB2BFamilyOffer: boolean;
    stepIndicator: StepIndicatorType;
    successfullySetAccountId: boolean;
    successfullySetPersonalDetails: boolean;
}

type Props = StateProps;

const PersonalDetails: FC<Props> = ({
    canContinuePersonalDetails,
    legalOwnerFullName,
    legalOwnerCity,
    legalOwnerAddress,
    isFetchingData,
    hideExistingPhoneNumberArea,
    successfullySetPersonalDetails,
    isBankIdAvailable,
    creditCheckMessage,
    isAllowedToProceed,
    isCreditCheckPerformed,
    isBankIdMandatory,
    isCreditCheckRequired,
    isHardwareOnly,
    isHwOnlyBankid,
    isDsfMismatch,
    logicalErrorMessage,
    shouldOnlyShowSlideoutCart,
    hasSubscription,
    hasOnlyServiceSubscriptions,
    hasBroadbandSubscriptions,
    stepIndicator,
    hasExternalLegal,
    isBankIdVerified,
    lastAddedCartItem,
    successfullySetAccountId,
    accountIdsWithTypes,
    initiatedBy,
    isMinSideSimdealOrder,
    showB2BFamilyOffer,
    accountId,
    numberSelectedAtPDP,
    hasOnlyPrepaidSubscriptions,
}) => {
    const [showLogOutBankIdModal, setShowLogoutBankIdModal] = useState(false);
    const [showBanModal, setShowBanModal] = useState(false);
    const location = useLocation();
    const dispatch = useDispatch();
    const numberOfCartSubscriptions = _size(useSelector((state: RootState) => getSubscriptions(state)));

    useEffect(() => {
        // Track begin_checkout
        if (isBankIdVerified) dispatch(trackCheckout(GTM_ECOMMERCE_STEP.LEGAL, initiatedBy));
        else dispatch(trackCheckout(GTM_ECOMMERCE_STEP.PERSONAL_DETAILS, initiatedBy));
    }, [isBankIdVerified, initiatedBy]);

    useEffect(() => {
        if (isHardwareOnly && successfullySetPersonalDetails) {
            dispatch(navigate(PAGES.CHECKOUT.SUMMARY.URL));
        } else if (successfullySetPersonalDetails && successfullySetAccountId) {
            const nextUrl =
                hasSubscription && !hasBroadbandSubscriptions
                    ? PAGES.CHECKOUT.ADD_MEMBER.URL
                    : PAGES.CHECKOUT.SUMMARY.URL;
            dispatch(navigate(nextUrl));
        }
    }, [
        successfullySetPersonalDetails,
        hasSubscription,
        hasBroadbandSubscriptions,
        successfullySetAccountId,
        navigate,
        isHardwareOnly,
    ]);

    const shouldShowExistingPrepaidKBDAboveLimitAlert = () => {
        return hideExistingPhoneNumberArea;
    };

    const renderExistingAlerts = () => {
        if (shouldShowExistingPrepaidKBDAboveLimitAlert()) {
            return (
                <Container className="beta-checkout--center" size="small" noMargin noPadding>
                    <Alert kind="warning">
                        <div
                            id="existing-prepaid-kbd-above-limit"
                            className="paragraph paragraph--fineprint"
                            dangerouslySetInnerHTML={{
                                __html: TEXT.PERSONAL_DETAILS.VERIFICATION.EXISTING_PREPAID_KBD_ABOVE_LIMIT,
                            }}
                        />
                    </Alert>
                </Container>
            );
        }

        if (logicalErrorMessage) {
            return (
                <Container size="small">
                    <Alert kind="warning">{logicalErrorMessage}</Alert>
                </Container>
            );
        }
    };

    const onContinueFromChooseBanModal = (id: string) => {
        if (!isHardwareOnly) {
            dispatch(setActiveSubscription(undefined));
            dispatch(checkBan(id));
        }
    };

    const isBanFull = (ban: AccountIdWithType) =>
        ban.subscriptions.length + numberOfCartSubscriptions > MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS;

    const allAccountsFull = accountIdsWithTypes.length > 0 && accountIdsWithTypes.every(isBanFull);

    const checkCustomerBanBeforeContinue = async () => {
        if (!isHardwareOnly) {
            if (hasOnlyPrepaidSubscriptions) {
                return dispatch(checkBan(NEW_ACCOUNT_VALUE));
            } else if (allAccountsFull) {
                return dispatch(checkBan(NEW_ACCOUNT_VALUE));
            } else if (_size(accountIdsWithTypes) === 1) {
                return dispatch(checkBan(accountIdsWithTypes[0].accountId));
            } else if (_size(accountIdsWithTypes) === 0) {
                return dispatch(checkBan(NEW_ACCOUNT_VALUE));
            } else if (showB2BFamilyOffer && accountId) {
                return dispatch(checkBan(accountId));
            } else if (numberSelectedAtPDP) {
                const accountOfNumberSelected = _find(accountIdsWithTypes, (account) =>
                    account.subscriptions.some((sub) => sub.phoneNumber === numberSelectedAtPDP),
                )?.accountId;
                return dispatch(checkBan(accountOfNumberSelected!));
            } else {
                return setShowBanModal(true);
            }
        }
    };

    const onSubmitPersonalDetails = async () => {
        await checkCustomerBanBeforeContinue();
        dispatch(setPersonalDetails());
    };

    const existingAlert = renderExistingAlerts();
    const [showCreditMessageModal, setShowCreditMessageModal] = useState(false);

    useEffect(() => {
        if (
            creditCheckMessage &&
            creditCheckMessage !== "OK" &&
            isAllowedToProceed &&
            isCreditCheckRequired &&
            isBankIdVerified
        ) {
            setShowCreditMessageModal(true);
        }
    }, [creditCheckMessage, isAllowedToProceed, isCreditCheckRequired, isBankIdVerified]);

    let pageIndex = 1;
    if (hasExternalLegal && !hasOnlyServiceSubscriptions) {
        pageIndex = 2;
    }

    function shouldShowBankIdNotAvailableWhenMandatory() {
        return !isBankIdAvailable && isBankIdMandatory;
    }

    function renderBankIdAlerts() {
        if (shouldShowBankIdNotAvailableWhenMandatory()) {
            return (
                <Container size="small" noMargin noPadding>
                    <Alert kind="warning">
                        {TEXT.PERSONAL_DETAILS.VERIFICATION.BANK_ID_MANDATORY_BUT_NOT_AVAILABLE}
                    </Alert>
                </Container>
            );
        }

        if (isBankIdVerified && isCreditCheckRequired && isCreditCheckPerformed && !isAllowedToProceed) {
            return (
                <Container size="small" noMargin noPadding>
                    <Alert kind="negative">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: creditCheckMessage,
                            }}
                            data-di-mask
                        />
                    </Alert>
                </Container>
            );
        }

        return null;
    }

    const bankIdAlert = renderBankIdAlerts();

    return (
        <CheckoutPage
            pageHeading={isHardwareOnly ? TEXT.PERSONAL_DETAILS.HEADING(true) : ""}
            isFetchingData={isFetchingData}
            isReadyToContinue={!shouldShowBankIdNotAvailableWhenMandatory()}
            pageIndex={pageIndex}
            stepIndicator={stepIndicator}
            path={location.pathname}
            lastAddedCartItem={lastAddedCartItem}
            hasSubscription={hasSubscription}
            hasCreditCheckFailed={isBankIdVerified && !isAllowedToProceed && isCreditCheckPerformed}
            shouldOnlyShowSlideoutCart={shouldOnlyShowSlideoutCart}
            initiatedBy={initiatedBy}
            isMinSideSimdealOrder={isMinSideSimdealOrder}
            resetBankId={() => dispatch(resetOrder())}
            emptyOrder={() => dispatch(emptyOrder())}
        >
            {showCreditMessageModal && (
                <CreditCheckModal
                    creditCheckMessage={creditCheckMessage}
                    onContinue={() => {
                        setShowCreditMessageModal(false);
                    }}
                />
            )}
            {bankIdAlert || existingAlert}
            {!existingAlert && !isDsfMismatch && !isBankIdVerified && (!isHardwareOnly || isHwOnlyBankid) && (
                <Container className="beta-checkout__bank-id-verification">
                    <BankIdSection typeId="co_bankid" referer="shop" />
                </Container>
            )}
            {isDsfMismatch && (
                <DsfMismatchForm
                    update={async (firstName, lastName) => dispatch(bankIdHandleDsfMismatch(firstName, lastName))}
                    setShowLogoutBankIdModal={(show) => setShowLogoutBankIdModal(show)}
                />
            )}
            {!existingAlert && !bankIdAlert && isBankIdVerified && (!isHardwareOnly || isHwOnlyBankid) && (
                <ContactCard
                    headingText={TEXT.PERSONAL_DETAILS.LEGAL_OWNER.HEADING_BANK_ID_VERIFIED}
                    name={legalOwnerFullName}
                    address={legalOwnerAddress}
                    city={legalOwnerCity}
                    resetBankId={() => setShowLogoutBankIdModal(true)}
                    showContactInfo={false}
                    showLogoutBankId={true}
                />
            )}
            {!existingAlert &&
                !bankIdAlert &&
                isBankIdVerified &&
                canContinuePersonalDetails &&
                (!isHardwareOnly || isHwOnlyBankid) && (
                    <Container
                        className="beta-checkout__section"
                        size="small"
                        whiteBg
                        noPaddingTop
                        data-cy="contact-info-form"
                    >
                        <ContactInfoForm onSubmit={onSubmitPersonalDetails} headingText="Kontaktinformasjon:" />
                    </Container>
                )}
            {isHardwareOnly && !isHwOnlyBankid && (
                <PersonalDetailsForm onSubmitPersonalDetails={onSubmitPersonalDetails} />
            )}
            <MobilPantNotificationWarning />
            {showLogOutBankIdModal && (
                <ModalDialog
                    className="contact-card-container__logout-bankid"
                    size="small"
                    submitText="Ja, logg meg ut"
                    closeText="Nei, bli på siden"
                    closeKind="secondary"
                    heading="Ønsker du å logge ut av BankID?"
                    name="log-out-bank-id-modal"
                    onSubmit={() => {
                        dispatch(resetOrder());
                        setShowLogoutBankIdModal(false);
                    }}
                    onClose={() => setShowLogoutBankIdModal(false)}
                ></ModalDialog>
            )}
            <ChooseBanModal
                numberOfCartSubscriptions={numberOfCartSubscriptions}
                shouldShow={showBanModal}
                onClose={() => setShowBanModal(false)}
                onContinue={onContinueFromChooseBanModal}
            />
        </CheckoutPage>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => {
    const currentCustomerType = CUSTOMER_INFO_SELECTORS.getCurrentType(state);

    return {
        // UI
        hideExistingPhoneNumberArea: COMBINED_SELECTORS.hideExistingPhoneNumberArea(state), // Due to existing KBD and phone price above MAX_PRICE_FOR_NEW_NUMBER

        // Loading
        isFetchingData: LOADING_SELECTORS.isLoading(state),

        // Authentication
        isBankIdVerified: isBankIdVerifiedSelector(state),
        isBankIdMandatory: isBankIdMandatorySelector(state),
        isBankIdAvailable: isBankIdAvailableSelector(state),

        // Verification
        successfullySetPersonalDetails: VERIFICATION_SELECTORS.successfullySetPersonalDetails(state),
        creditCheckMessage: VERIFICATION_SELECTORS.creditCheckMessage(state),
        isCreditCheckPerformed: VERIFICATION_SELECTORS.isCreditCheckPerformed(state),
        isAllowedToProceed: VERIFICATION_SELECTORS.isAllowedToProceed(state),
        isCreditCheckRequired: VERIFICATION_SELECTORS.isCreditCheckRequired(state),
        isDsfMismatch: VERIFICATION_SELECTORS.isDsfMismatch(state),

        canContinuePersonalDetails: canContinuePersonalDetailsSelector(state),
        successfullySetAccountId: VERIFICATION_SELECTORS.successfullySetAccountId(state),

        // Cart
        lastAddedCartItem: lastAddedCartItemSelector(state),
        isHardwareOnly: COMBINED_SELECTORS.isHardwareOnly(state),
        isHwOnlyBankid: COMBINED_SELECTORS.isHwOnlyButRequireBankid(state),
        isSimOnly: COMBINED_SELECTORS.isSimOnly(state),
        shouldOnlyShowSlideoutCart: COMBINED_SELECTORS.shouldOnlyShowSlideoutCart(state),

        // Subscriptions info
        hasSubscription: hasSubscriptions(state),
        hasBroadbandSubscriptions: COMBINED_SELECTORS.hasBroadbandInCart(state),
        hasOnlyServiceSubscriptions: hasOnlyServiceSubscriptionsSelector(state),
        numberSelectedAtPDP: getNumberSelectedAtPDP(state),
        hasOnlyPrepaidSubscriptions: hasOnlyPrepaidSubscriptionsSelector(state),

        // Customer info
        customerType: currentCustomerType,
        legalOwnerFullName: CUSTOMER_INFO_SELECTORS.getLegalOwnerFullName(state),
        legalOwnerCity: CUSTOMER_INFO_SELECTORS.getLegalOwnerFullCity(state),
        legalOwnerAddress: CUSTOMER_INFO_SELECTORS.getLegalOwnerAddress(state),

        // Account info
        accountId: getAccountId(state),
        hasExternalLegal: hasExternalLegalOwner(state),
        accountIdsWithTypes: getAccountsWithSubscriptions(state),
        initiatedBy: getInitiatedBy(state),
        isMinSideSimdealOrder: isMinSideSimdealOrderSelector(state),
        showB2BFamilyOffer: getExternalShowB2BFamilyOffer(state),
        stepIndicator: getStepIndicatorData(state),

        // Other
        logicalErrorMessage: getErrorMessage(state),
    };
};

export default connect(mapStateToProps)(PersonalDetails);
